// Stamp a faded DOLLUZ CORP logo watermark on every page of the given jsPDF
// document. Call AFTER all content is drawn. Wrapped in try/catch so older
// jsPDF builds without GState degrade silently (no watermark).
// Copied from dAdmin (src/utils/pdfWatermark.js) so exports look the same.

import full_logo from "../assets/img/DOLLUZ_Full_Logo.png";

// jsPDF can't desaturate an image, and the logo is gold. Pre-render a grayscale
// copy once (via canvas) so the watermark reads gray. Loads async on import; if
// it isn't ready yet we fall back to the colour logo.
let grayLogo = null;
(function prepGrayLogo() {
    if (typeof document === "undefined") return;
    const img = new Image();
    img.onload = () => {
        try {
            // dForms: drawn at most 900px wide (the full-size logo made each
            // page carry megabytes); a faint watermark needs no more.
            const scale = Math.min(1, 900 / (img.naturalWidth || 512));
            const c = document.createElement("canvas");
            c.width = Math.round((img.naturalWidth || 512) * scale);
            c.height = Math.round((img.naturalHeight || 512) * scale);
            const ctx = c.getContext("2d");
            ctx.filter = "grayscale(1)";
            ctx.drawImage(img, 0, 0, c.width, c.height);
            // Fade the BOTTOM of the logo (the "DOLLUZ CORP" wordmark + tagline)
            // progressively more than the top, so the lower part is barely
            // there. destination-out erases the logo in proportion to the
            // gradient's alpha.
            ctx.filter = "none";
            ctx.globalCompositeOperation = "destination-out";
            const fade = ctx.createLinearGradient(0, 0, 0, c.height);
            fade.addColorStop(0, "rgba(0,0,0,0)");
            fade.addColorStop(0.55, "rgba(0,0,0,0)");
            fade.addColorStop(1, "rgba(0,0,0,0.62)");
            ctx.fillStyle = fade;
            ctx.fillRect(0, 0, c.width, c.height);
            ctx.globalCompositeOperation = "source-over";
            grayLogo = c.toDataURL("image/png");
        } catch (_) { /* keep null - fall back to colour */ }
    };
    img.src = full_logo;
})();

// DOLLUZ_Full_Logo.png is ~square (1932 x 1947) and already contains the eagle,
// the "DOLLUZ CORP" wordmark and the tagline, so no separate text is drawn.
const LOGO_ASPECT = 1947 / 1932;   // height / width

export function addEagleWatermark(doc, options = {}) {
    const {
        opacity = 0.14,         // logo opacity (grayscale washes it lighter, so higher)
        sizeRatio = 0.55,       // logo width as a fraction of page width
        offsetY = 0,            // shift the logo up (negative) / down (positive), page units
        topY = null,            // if set, anchor the TOP of the logo here (page units) instead
                                // of vertically centering it
    } = options;

    try {
        const GState = doc.GState || (typeof window !== "undefined" && window.GState);
        if (!GState || typeof doc.saveGraphicsState !== "function") return;

        const totalPages = doc.internal.getNumberOfPages();
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // Remember current page so we can restore it after stamping
        const originalPage = doc.internal.getCurrentPageInfo
            ? doc.internal.getCurrentPageInfo().pageNumber
            : 1;

        for (let p = 1; p <= totalPages; p++) {
            doc.setPage(p);
            doc.saveGraphicsState();

            // Logo - centered, sized as a fraction of the page width
            doc.setGState(new GState({ opacity }));
            const wmW = pageW * sizeRatio;
            const wmH = wmW * LOGO_ASPECT;
            const wmX = (pageW - wmW) / 2;
            const wmY = (topY != null ? topY : (pageH - wmH) / 2) + offsetY;
            // One alias: the image is stored in the file once, not per page.
            doc.addImage(grayLogo || full_logo, "PNG", wmX, wmY, wmW, wmH, grayLogo ? "dolluz-watermark" : "dolluz-watermark-colour", "FAST");

            doc.restoreGraphicsState();
        }

        // Restore the page cursor so subsequent draws don't write to the last page
        doc.setPage(originalPage);
    } catch (_) {
        // Decorative; never block the PDF render
    }
}
